import { FunctionComponent } from 'preact';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'preact/hooks';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import { useStyles } from '../../../common/hooks/useStyles';
import { getSkippableContentId } from '../htmlElements/htmlElementsIdGetter';
import {
  playNextVideo,
  replayVideo,
} from '../store/discoveryPlugin.actions';
import { discoverySelector } from '../../../common/store/selectors/discovery.selector';
import { mediaDataSelector } from '../../../common/store/selectors/mediaData.selector';
import { NextVideo } from '../nextVideo/nextVideo';
import { css } from '../../../common/styles/styles.utils';
import { getSkippableContentStyleObject } from './skippableContent.style';
import { KeepWatchingButtons } from './keepWatchingButtons/keepWatchingButtons';
import {
  DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_BUTTON_AFTER_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_DEFAULT_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_IN_TOTAL_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_KEEP_WATCHING_DEFAULT_SECONDS,
} from '../discoveryPlugin.constants';
import { KeepWatchingButton } from './keepWatchingButton/keepWatchingButton';
import { moreVideosSelector } from '../../../common/store/selectors/moreVideos.selector';

export const SkippableContent: FunctionComponent = () => {
  const dispatch = useDispatch();
  const playerUniqueId = useSelector(playerDataSelector.playerInstanceUniqId);
  const playerSize = useSelector(playerDataSelector.playerSize);
  const currentVideoTimeFragment = useSelector(playerDataSelector.currentVideoTimeFragment);
  const showSkippableContent = useSelector(discoverySelector.showSkippableContent);
  const skippableContentTime = useSelector(discoverySelector.skippableContentTime);
  const videoList = useSelector(mediaDataSelector.videoList);
  const galleryView = useSelector(moreVideosSelector.galleryView);
  const showGalleryView = useSelector(moreVideosSelector.showGalleryView);

  const [showKeepWatching, setShowKeepWatching] = useState(false);

  const keepWatchingButtonsTime = skippableContentTime === DISCOVERY_SHOW_SKIPPABLE_CONTENT_DEFAULT_SECONDS ? DISCOVERY_SHOW_SKIPPABLE_CONTENT_KEEP_WATCHING_DEFAULT_SECONDS : skippableContentTime;
  const showOnlyKeepWatchingButtons = playerSize === 'small';
  const isSingleVideo = videoList.length === 1;

  const startTimeShowSkippable = showOnlyKeepWatchingButtons ? keepWatchingButtonsTime : skippableContentTime;
  const totalTimeShowSkippable = showOnlyKeepWatchingButtons ? DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS : DISCOVERY_SHOW_SKIPPABLE_CONTENT_IN_TOTAL_SECONDS;
  const isInRange = currentVideoTimeFragment >= startTimeShowSkippable &&
    currentVideoTimeFragment <= startTimeShowSkippable + totalTimeShowSkippable + 0.5;
  const countdownTime = showSkippableContent && isInRange ? currentVideoTimeFragment - startTimeShowSkippable : 0;
  const smallSkippableContent = playerSize === 'medium' || (playerSize === 'large' && galleryView);

  const styles = useStyles(getSkippableContentStyleObject, [playerSize, showSkippableContent, smallSkippableContent, showGalleryView]);
  const { skippableContentContainerStyle } = styles;

  useEffect(() => {
    if (countdownTime >= totalTimeShowSkippable) {
      if (isSingleVideo) {
        dispatch(replayVideo());
      } else {
        dispatch(playNextVideo());
      }
    }
  }, [countdownTime, isSingleVideo, dispatch, totalTimeShowSkippable]);

  useEffect(() => {
    if (currentVideoTimeFragment >= skippableContentTime + DISCOVERY_SHOW_SKIPPABLE_CONTENT_BUTTON_AFTER_SECONDS) {
      setShowKeepWatching(true);
    }
    if (currentVideoTimeFragment === 0) {
      setShowKeepWatching(false);
    }
  }, [currentVideoTimeFragment, skippableContentTime]);

  return (
    <div id={getSkippableContentId(playerUniqueId)} style={css(skippableContentContainerStyle)}>
      {showOnlyKeepWatchingButtons ?
        <KeepWatchingButtons /> :
        (
          <div>
            <NextVideo countdownTime={countdownTime} />
            <KeepWatchingButton showKeepWatching={showKeepWatching} />
          </div>
        )}
    </div>
  );
};
